import type { RecipeMainIngredient } from '@apps/main/modules/recipe/ingredients';

import type { ConnectionType } from '@libs/maps';
import type { MonthIndex } from '@libs/sembo-dates';
import type {
  AvailabilityOrder,
  Landing,
  RecipeMetaData,
  Pax,
} from '@libs/trip-planner-interfaces';

export enum TripPlannerPage {
  BaseURL = 'plan',
  BuildYourTrip = 'buildYourTrip',
  TripOverview = 'tripOverview',
  TripOverviewPrint = 'tripOverviewPrint',
  FlightList = 'flights',
  FerryList = 'ferry',
  HotelSelection = 'hotelSelection',
  ActivitySelection = 'activitySelection',
  HotelDetailsInHotelSelection = 'hotelDetailsInHotelSelection',
  HotelDetails = 'hotelDetails',
  ActivityDetailsInActivitySelection = 'activityDetailsInActivitySelection',
  ActivityDetails = 'activityDetails',
  CarTrip = 'carTrip',
}

// NOTE: This parameter is used when some errors further down the funner occured
// and user is redirected back to trip planner to try again.
export const enum ExternalErrorQueryParamValue {
  // NOTE: This variant is used by book team to redirect user from checkout, when booking failed.
  BookingFailed = 'booking-failed',
}

// NOTE: This parameter is used when user is redirected to trip planner to to buy additional products.
export const enum CustomerOriginQueryParamValue {
  // NOTE: This variant is used by shop team to upsell accommodation for users who already bought transport only.
  BookingPage = 'booking-page',
  // NOTE: redirects from the destination search component
  MarketplaceDestinationSearch = 'marketplace-destination-search',
  // NOTE: redirects from flight search component
  MarketplaceFlightSearch = 'marketplace-flight-search',
  // NOTE: redirects from the "By car" tab and SideMenu
  MarketplaceCar = 'marketplace-car',
  // NOTE: redirects from Amelia
  MarketplaceAi = 'marketplace-ai',
  // NOTE: redirects from Flyer, Places, and HotelPlaces pages
  MarketplaceFlyer = 'marketplace-flyer',
  // NOTE: redirects from recently viewed components
  MarketplaceRecentlyViewed = 'marketplace-recently-viewed',
  // NOTE: redirects from saved trip components
  MarketplaceSavedTrip = 'marketplace-saved-trip',
  // NOTE: redirects from the Stenaline creator page (https://www.sembo.se/creator/stenaline-sverige)
  MarketplaceFerryHotelSearch = 'marketplace-ferry-hotel-search',
  // NOTE: redirects from the stenaline-search-component
  StenalineSearch = 'stenaline-search-component',
  // NOTE: used in legacy or uncertain flows (e.g. short URL service)
  MarketplaceGeneric = 'marketplace-generic',
  // NOTE: used on reload modal click for infinite load
  InfiniteLoadReload = 'infinite-load-reload',
}

export const enum ConfigurationPanelQueryParamValue {
  Calendar = 'calendar',
}

export interface TripPlannerQueryParams {
  initialIngredients: RecipeMainIngredient[];
  initialPax: Pax;
  initialSelectedDate?: Date;
  initialSelectedMonth?: MonthIndex;
  initialSuggestionSortType?: AvailabilityOrder;
  landing?: Landing;
  initialMetaRooms?: string[];
  sessionId?: string;
  initialRedirectionToken?: string;
  returnTransport?: ConnectionType;
  metaData?: RecipeMetaData;
  aiTripId?: string;
  publishedTripId?: string;
  externalError?: ExternalErrorQueryParamValue;
  customerOrigin?: CustomerOriginQueryParamValue;
  panel?: ConfigurationPanelQueryParamValue;
  searchForPackageRates?: boolean | null;
}

const simplePagePaths = new Map<string, TripPlannerPage>([
  ['travel/plan', TripPlannerPage.BaseURL],
  ['travel/plan/build-your-trip', TripPlannerPage.BuildYourTrip],
  ['travel/plan/overview', TripPlannerPage.TripOverview],
  ['travel/plan/overview-print', TripPlannerPage.TripOverviewPrint],
  ['travel/plan/hotel-details', TripPlannerPage.HotelDetails],
  ['travel/plan/flights', TripPlannerPage.FlightList],
  ['travel/plan/ferry', TripPlannerPage.FerryList],
  ['travel/plan/activity-details', TripPlannerPage.ActivityDetails],
  ['travel/plan/car-trip', TripPlannerPage.CarTrip],
]);

const parametrizedPagePaths = new Map<RegExp, TripPlannerPage>([
  [
    /^travel\/plan\/hotel-selection\/h?([0-9]+)$/,
    TripPlannerPage.HotelSelection,
  ],

  [
    /^travel\/plan\/hotel-selection\/h?([0-9]+)\/hotel-details\/.+$/,
    TripPlannerPage.HotelDetailsInHotelSelection,
  ],
  [
    /^travel\/plan\/activity-selection\/h?([0-9]+)$/,
    TripPlannerPage.ActivitySelection,
  ],
  [
    /^travel\/plan\/activity-selection\/h?([0-9]+)\/activity-details\/.+$/,
    TripPlannerPage.ActivityDetailsInActivitySelection,
  ],
]);

export function mapPathToTripPlannerPage(
  path: string
): TripPlannerPage | undefined {
  // NOTE: remove trailing and leading slashes
  const sanitizedPath = path.replace(/\/$/, '').replace(/^\//, '');

  const simplePage = simplePagePaths.get(sanitizedPath);

  if (simplePage) {
    return simplePage;
  }

  for (const [pathRegex, parametrizedPage] of parametrizedPagePaths) {
    if (pathRegex.test(sanitizedPath)) {
      return parametrizedPage;
    }
  }

  return undefined;
}
